// The Floor screen's live push (spec/09 §4, §6.3; F-STN-14). While the organisation's realtime
// socket is live, this subscribes to `venue:{v}:floor` and drives the snapshot-then-stream sequencer
// (`createSnapshotStream`, `@clavian/api-client`): the REST snapshot is the base, `floor.station.updated`
// deltas are applied in version order, and a version gap or a `settings.changed` fetches the snapshot
// again — there is no server-side replay for consoles (spec/09 §4). Each new view is written into the
// same React Query cache the Floor reads, so the tiles update without a refetch. When the socket is
// not live (reduced motion, or disconnected) this does nothing and the Floor polls (`queries.ts`).

import { createSnapshotStream, type FloorSnapshot, type FloorStationTitle, type SnapshotStream, type StationCall, type StationCallList } from '@clavian/api-client';
import {
  CALLS_CLOSED,
  CALLS_OPENED,
  CALLS_UPDATED,
  channels,
  COMMAND_UPDATED,
  FLOOR_BOOT_CHANGED,
  FLOOR_PLAN_UPDATED,
  FLOOR_SESSION_ENDED,
  FLOOR_SESSION_STARTED,
  FLOOR_STATION_UPDATED,
} from '@clavian/contracts/constants';
import type { CallEventPayload, CommandUpdatedPayload, FloorPlanUpdatedPayload, FloorStationUpdatedPayload, RealtimeEnvelope } from '@clavian/contracts/realtime';
import type { TypeId } from '@clavian/contracts/typeid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useApi } from '../api/client';
import { fetchMyAccess } from '../api/me';
import { bootKeys } from '../boot/queries';
import { queryKeys } from '../api/queries';
import { useChannelEvents, useRealtimeLive } from '../realtime';
import { floorKeys } from './queries';

/** The channel version an event advances, or `undefined` for one outside the sequence (settings.changed). */
function floorEventVersion(envelope: RealtimeEnvelope): number | undefined {
  if (envelope.type !== FLOOR_STATION_UPDATED) return undefined;
  const version = (envelope.p as FloorStationUpdatedPayload | undefined)?.version;
  return typeof version === 'number' ? version : undefined;
}

/** The floor a `floor.plan.updated` frame names (F-STN-19), or `undefined` for anything else. */
export function floorPlanUpdateOf(envelope: RealtimeEnvelope): number | undefined {
  if (envelope.type !== FLOOR_PLAN_UPDATED) return undefined;
  const floor = (envelope.p as FloorPlanUpdatedPayload | undefined)?.floor;
  return typeof floor === 'number' && Number.isInteger(floor) ? floor : undefined;
}

/** The snapshot tile's `title` from a delta's: the same `{ titleId, exe, name?, since? }` (F-CAT-04). */
function titleOf(title: FloorStationUpdatedPayload['title'] | undefined): FloorStationTitle | null {
  if (title === null || title === undefined) return null;
  return {
    titleId: title.titleId,
    exe: title.exe,
    ...(title.name === undefined ? {} : { name: title.name }),
    ...(title.since === undefined ? {} : { since: title.since }),
  };
}

/**
 * Apply one `floor.station.updated` delta to a snapshot: the station's online flag, its mode, what it is running now and —
 * while it is setting up (ADR-0055) — how far its start-up setup has got. Idempotent, so the Floor's two live hooks
 * (`useFloorLive`, `useSessionsLive`) can patch the same cache entry from the same event. A delta from a gateway that
 * predates `setup` carries none: the setup is then whatever the mode allows (none outside `setting_up`).
 */
export function applyFloorDelta(view: FloorSnapshot, delta: FloorStationUpdatedPayload): FloorSnapshot {
  const stations = view.stations.map((station) =>
    station.stationId === delta.stationId
      ? {
          ...station,
          online: delta.online,
          mode: delta.mode,
          title: titleOf(delta.title),
          setup: delta.mode === 'setting_up' ? (delta.setup ?? (station.mode === 'setting_up' ? (station.setup ?? null) : null)) : null,
        }
      : station,
  );
  return { ...view, stations };
}

function applyFloorEvent(view: FloorSnapshot, envelope: RealtimeEnvelope): FloorSnapshot {
  return applyFloorDelta(view, envelope.p as FloorStationUpdatedPayload);
}

/**
 * Keep the Floor snapshot live from the channel while the socket is live. Returns whether the push
 * is active, so the Floor can drop its interval and show a subtle live state.
 */
export function useFloorLive(organizationId: string, venueId: string): boolean {
  const api = useApi();
  const queryClient = useQueryClient();
  const live = useRealtimeLive();
  const stream = useRef<SnapshotStream<FloorSnapshot, RealtimeEnvelope> | undefined>(undefined);

  useChannelEvents(
    channels.venue(venueId as TypeId<'ven'>, 'floor'),
    (envelope) => {
      // A saved plan (F-STN-19): re-read that floor's plan, not the whole snapshot — the tiles did not change.
      if (envelope.type === FLOOR_PLAN_UPDATED) {
        const floor = floorPlanUpdateOf(envelope);
        if (floor !== undefined) void queryClient.invalidateQueries({ queryKey: floorKeys.plan(organizationId, venueId, floor) });
        return;
      }
      // A PC's boot state may have changed (Clavian Boot): the simple states a member without `boot:view` reads are read
      // again. The frame carries no boot detail, and the tiles' other state did not change.
      if (envelope.type === FLOOR_BOOT_CHANGED) {
        void queryClient.invalidateQueries({ queryKey: bootKeys.floorStates(organizationId, venueId) });
        return;
      }
      stream.current?.receive(envelope);
    },
    live,
  );

  useEffect(() => {
    if (!live) return undefined;
    const key = floorKeys.snapshot(organizationId, venueId);
    const sequencer = createSnapshotStream<FloorSnapshot, RealtimeEnvelope>({
      fetchSnapshot: async () => {
        const snapshot = await queryClient.fetchQuery({ queryKey: key, queryFn: () => api.getFloor({ path: { organizationId, venueId } }) });
        return { snapshot, version: snapshot.version };
      },
      versionOf: floorEventVersion,
      apply: applyFloorEvent,
      onView: (view) => queryClient.setQueryData(key, view),
      // Outside the version sequence (a setting changed): refetch what this member may see.
      onUnversioned: () => void queryClient.invalidateQueries({ queryKey: key }),
    });
    stream.current = sequencer;
    void sequencer.start();
    return () => {
      sequencer.stop();
      stream.current = undefined;
    };
  }, [live, organizationId, venueId, api, queryClient]);

  return live;
}

/** How many calls the list keeps once live frames start adding to it (the REST list's own window is wider). */
const CALL_LIST_LIMIT = 200;

/**
 * Apply one `calls.opened` / `calls.updated` / `calls.closed` frame to the venue's call list (F-SES-25, spec/09 §6.3):
 * the frame carries the whole call, so it replaces the card with the same id, or is added at the top (newest first).
 * Pure; a frame with no call leaves the list as it is.
 */
export function applyCallEvent(list: StationCallList | undefined, envelope: RealtimeEnvelope): StationCallList | undefined {
  if (envelope.type !== CALLS_OPENED && envelope.type !== CALLS_UPDATED && envelope.type !== CALLS_CLOSED) return list;
  const call = (envelope.p as CallEventPayload | undefined)?.call as StationCall | undefined;
  if (call === undefined || typeof call.id !== 'string') return list;
  const calls = list?.calls ?? [];
  const known = calls.some((existing) => existing.id === call.id);
  const next = known ? calls.map((existing) => (existing.id === call.id ? call : existing)) : [call, ...calls];
  return { ...list, calls: next.slice(0, CALL_LIST_LIMIT) };
}

/**
 * Keep the venue's calls live from `venue:{v}:calls` while the socket is live (F-SES-25, F-KIO-17): a call raised on a
 * PC flags its tile and lands in "What needs you" within a second, "On my way" and Done update it, with no polling —
 * `useCalls` stops its interval while live. On going live the list is read once, so nothing missed while offline
 * lingers (spec/09 §4: the REST list is the recovery). Needs `calls:read` (the gateway checks it).
 */
export function useCallsLive(organizationId: string, venueId: string, enabled: boolean): void {
  const queryClient = useQueryClient();
  const live = useRealtimeLive();
  const key = floorKeys.calls(organizationId, venueId);

  useChannelEvents(
    channels.venue(venueId as TypeId<'ven'>, 'calls'),
    (envelope) => queryClient.setQueryData<StationCallList>(key, (list) => applyCallEvent(list, envelope)),
    live && enabled,
  );

  useEffect(() => {
    if (live && enabled) void queryClient.invalidateQueries({ queryKey: floorKeys.calls(organizationId, venueId) });
  }, [live, enabled, organizationId, venueId, queryClient]);
}

/**
 * When the player acknowledged a staff message this member sent (F-STN-20, spec/09 §6.2): the station reports
 * Got it on the kiosk or the overlay hotkey, the api records it on the `session.message` command and sends
 * this member `command.updated` with `result.readAt`. `null` until then (and when the socket is not live —
 * the read is still recorded on the command). Follows one command at a time: a new id starts over.
 */
export function useStaffMessageRead(venueId: string, commandId: string | null): string | null {
  const api = useApi();
  const live = useRealtimeLive();
  const access = useQuery({ queryKey: queryKeys.access({ venueId }), queryFn: () => fetchMyAccess(api, { venueId }), retry: false });
  const memberId = access.data?.memberId ?? null;
  const [read, setRead] = useState<{ readonly commandId: string; readonly at: string } | null>(null);

  useChannelEvents(
    channels.member((memberId ?? '') as TypeId<'mem'>),
    (envelope) => {
      if (envelope.type !== COMMAND_UPDATED) return;
      const payload = envelope.p as CommandUpdatedPayload | undefined;
      const readAt = payload?.result?.['readAt'];
      if (payload?.commandId === commandId && typeof readAt === 'string') setRead({ commandId: payload.commandId, at: readAt });
    },
    live && memberId !== null && commandId !== null,
  );

  return read !== null && read.commandId === commandId ? read.at : null;
}

/**
 * Keep the floor's live figures current (F-ANL-02): a session starting or ending at the venue
 * (`floor.session.started` / `ended` on `venue:{v}:floor`) re-reads them, so the occupancy count and takings move
 * within a second with no page refetch. The figures' own interval catches a missed frame and the counter sales that
 * start no session.
 */
export function useFloorFiguresLive(organizationId: string, venueId: string, enabled: boolean): void {
  const queryClient = useQueryClient();
  const live = useRealtimeLive();
  useChannelEvents(
    channels.venue(venueId as TypeId<'ven'>, 'floor'),
    (envelope) => {
      if (envelope.type !== FLOOR_SESSION_STARTED && envelope.type !== FLOOR_SESSION_ENDED) return;
      void queryClient.invalidateQueries({ queryKey: floorKeys.figures(organizationId, venueId) });
    },
    live && enabled,
  );
}
